/**
 * PROVENANCE ON THE WIRE — the contract, not a convention.
 *
 * FOUNDER, 2026-08-08: "make provenance a contract on the wire."
 *
 * ── THE PROBLEM THIS CLOSES ───────────────────────────────────────────────────────────────────
 * FP-018 says every fact is COMPUTED, QUOTED or REFUSED. Inside a service that is enforced where the
 * value is produced. Across a network boundary it was not enforced at all — it survived because both
 * ends happened to agree that `{kind:'null', reason}` meant a refusal, and the renderer sniffed for
 * that shape.
 *
 * A shape both ends happen to agree on is a convention. It holds until somebody writes a third
 * service, returns a bare `null`, and nothing anywhere notices — because a bare null is VALID JSON
 * and a valid GraphQL nullable field. The failure would look exactly like an empty cell.
 *
 * ── WHAT A BARE NULL CONFLATES ────────────────────────────────────────────────────────────────
 * Three different facts, and a reader needs to tell them apart:
 *
 *     REFUSED   the system could have answered and declined, and can say why
 *     ABSENT    there is genuinely no such value — an optional field nobody filled
 *     FAILED    something broke on the way
 *
 * The first is the product working correctly. The third is an incident. Rendering them the same is
 * the single easiest way to make an honest system look broken, or a broken one look honest.
 *
 * ── THE CONTRACT ──────────────────────────────────────────────────────────────────────────────
 * Every field that CAN abstain returns an envelope rather than a naked value:
 *
 *     { state: 'computed'|'cited'|'refused'|'absent'|'failed', value?, source?, reason?, at? }
 *
 * `computed` and `cited` carry a value. `refused` carries a REASON and never a value. `absent` and
 * `failed` carry neither. That is checkable, and `validate()` below checks it — which is what makes
 * this a contract rather than a stronger convention.
 *
 * ── WHY NOT A GraphQL DIRECTIVE ───────────────────────────────────────────────────────────────
 * A `@provenance` directive was the tempting shape and it is the wrong one: a directive is metadata
 * about the SCHEMA, and this is a property of the ANSWER. The same field is computed for one ship
 * and refused for another. It has to travel in the payload.
 */

/** The SIX states. Was five until 2026-08-13; GATED was added when the Academy began charging, and
    the note below is kept because it is what made that change safe: this file, the SDL and the
    validator all had to move together, and they did. */
export const STATES = Object.freeze({
  COMPUTED: 'computed',   // derived from declared data. Carries a value.
  CITED:    'cited',      // quoted from a named source. Carries a value AND a source.
  REFUSED:  'refused',    // declined, with a reason. NEVER carries a value.
  ABSENT:   'absent',     // no such value exists. Carries nothing.
  FAILED:   'failed',     // something broke. Carries a reason, never a value.
  /* GATED — the value EXISTS and is withheld. Added 2026-08-13, when the Academy began refusing
     chapter content outside its free tier.
     It is a sixth state rather than a reuse of REFUSED because those are opposite facts about
     KNOWLEDGE. `refused` says we do not hold this; `gated` says we hold it and you may not have it
     yet. Rendering a paywall as an abstention would teach a learner that the corpus lacks something
     it contains — the precise dishonesty FP-018 exists to prevent — and would also make the coverage
     figures unreadable, since a gap and a fee would look the same on screen.
     The file already draws this distinction once: "an outage is not an abstention. Same absence on
     screen, different fact, different chip." A paywall is neither of them.
     Carries a reason and NEVER a value: a gated envelope that leaked the content would defeat itself. */
  GATED:    'gated',
});
const ALL = Object.values(STATES);

/* ── constructors. Using these is how a service stays inside the contract without reading it. ── */
export const computed = (value, at) => ({ state: STATES.COMPUTED, value, ...(at ? { at } : {}) });
/* `free_instead` is not decoration: a gate that says only "no" teaches a learner he is unwelcome,
   while one that names what he CAN still read teaches him where the edge is. */
export const gated = (reason, extra = {}) => ({ state: STATES.GATED, reason, ...extra });
export const cited    = (value, source) => ({ state: STATES.CITED, value, source });
export const refused  = (reason) => ({ state: STATES.REFUSED, reason });
export const absent   = () => ({ state: STATES.ABSENT });
export const failed   = (reason) => ({ state: STATES.FAILED, reason });

/**
 * validate — the reason this is a contract.
 *
 * Returns [] when the envelope is legal, or the faults. Call it in a test, in CI, or in a gateway;
 * an unvalidated contract is a convention with better documentation.
 */
export function validate(env, path = 'field') {
  const f = [];
  if (env === null || env === undefined) {
    return [`${path}: bare null on the wire. A field that can abstain must return an envelope — a ` +
            `bare null conflates refused, absent and failed, which are three different facts.`];
  }
  if (typeof env !== 'object' || Array.isArray(env)) return [`${path}: not an envelope (${typeof env})`];
  if (!ALL.includes(env.state)) return [`${path}: unknown state ${JSON.stringify(env.state)}`];

  const has = (k) => Object.prototype.hasOwnProperty.call(env, k);
  switch (env.state) {
    case STATES.COMPUTED:
      if (!has('value')) f.push(`${path}: computed without a value`);
      break;
    case STATES.CITED:
      if (!has('value')) f.push(`${path}: cited without a value`);
      // A citation nobody can follow is not a citation. This is the clause that stops "cited" being
      // used as a synonym for "we are fairly sure".
      if (!env.source) f.push(`${path}: cited without a source — a citation a reader cannot follow ` +
                              `is not a citation`);
      break;
    case STATES.REFUSED:
      if (!env.reason) f.push(`${path}: refused without a reason — an abstention that does not say ` +
                              `what is missing teaches nothing and cannot be acted on`);
      if (has('value')) f.push(`${path}: refused WITH a value — the two cannot both be true`);
      break;
    case STATES.GATED:
      if (!env.reason) f.push(`${path}: gated without a reason — a refusal that does not say what ` +
                              `would open it is a locked door with no handle`);
      if (has('value')) f.push(`${path}: gated WITH a value — the withheld content is in the envelope, ` +
                               `so the gate has already leaked what it was protecting`);
      break;
    case STATES.ABSENT:
      if (has('value')) f.push(`${path}: absent with a value`);
      break;
    case STATES.FAILED:
      if (!env.reason) f.push(`${path}: failed without a reason`);
      if (has('value')) f.push(`${path}: failed with a value`);
      break;
  }
  return f;
}

/** Validate a whole payload. Only envelope-shaped members are checked; plain fields are left alone. */
export function validatePayload(obj, path = '$') {
  const f = [];
  const walk = (v, p) => {
    if (!v || typeof v !== 'object') return;
    if (Array.isArray(v)) return v.forEach((x, i) => walk(x, `${p}[${i}]`));
    if (typeof v.state === 'string' && ALL.includes(v.state)) { f.push(...validate(v, p)); return; }
    for (const [k, x] of Object.entries(v)) walk(x, `${p}.${k}`);
  };
  walk(obj, path);
  return f;
}

/**
 * ADAPTER for what already exists. The engines return `{kind:'null', reason}` today, and that shape
 * predates this contract and is in service. Normalising at the edge is what lets the contract be
 * adopted without a flag day — and without pretending the old shape is gone.
 */
export function normalise(v) {
  if (v && typeof v === 'object' && ALL.includes(v.state)) return v;          // already a contract
  if (v && typeof v === 'object' && v.kind === 'null') return refused(v.reason || 'no reason given');
  if (v && typeof v === 'object' && v.error) return failed(String(v.error));
  if (v === null || v === undefined) return absent();
  return computed(v);
}

/** The GraphQL side of the same contract. One definition, so the two cannot drift apart. */
export const SDL = `
"How this system knows a fact. FP-018: computed, quoted, or refused — never guessed."
enum ProvenanceState { COMPUTED CITED REFUSED ABSENT FAILED GATED }

"""
An answer with its provenance. Every field that CAN abstain returns this rather than a nullable
scalar, because a bare null conflates three different facts:
  REFUSED  the system declined and can say why   (correct behaviour)
  ABSENT   there is genuinely no such value      (nothing happened)
  FAILED   something broke                       (an incident)
"""
type Provenance {
  state: ProvenanceState!
  "Present for COMPUTED and CITED. Never present for REFUSED, ABSENT or FAILED."
  value: JSON
  "Required for CITED. A citation a reader cannot follow is not a citation."
  source: String
  "Required for REFUSED and FAILED. Names what is missing or what broke."
  reason: String
  at: String
}
`.trim();
